import { inject, Injector } from '@angular/core';

export abstract class ToastService {
    protected readonly injector = inject(Injector);

    /** How long the store-offline text stays suppressed after it was shown. Matches the interceptor's quiet period. */
    static readonly storeOfflineRepeatWindowMs = 60_000;

    private storeOfflineTexts: ReadonlySet<string> = new Set();
    private storeOfflineShownAt = Number.NEGATIVE_INFINITY;

    /**
     * Records that the store-offline notice was just shown, in every wording it may arrive in (the client's
     * dictionary text and the server's message). Many screens toast the server's error message themselves; while
     * the window lasts, {@link isRepeatOfStoreOffline} lets implementations drop those copies, so one outage reads
     * as one toast without changing each screen.
     *
     * @param texts - The wordings to suppress; empty values are ignored.
     * @param now - Current time in milliseconds; injectable for tests.
     */
    markStoreOfflineShown(texts: ReadonlyArray<string | null | undefined>, now: number = Date.now()): void {
        this.storeOfflineTexts = new Set(texts.filter((text): text is string => !!text));
        this.storeOfflineShownAt = now;
    }

    /**
     * True when an error toast would repeat the store-offline notice shown within the window. Implementations call
     * it first in {@link showError}. Any other message, or the same one after the window, is shown as normal.
     */
    protected isRepeatOfStoreOffline(message: string, now: number = Date.now()): boolean {
        return now - this.storeOfflineShownAt < ToastService.storeOfflineRepeatWindowMs && this.storeOfflineTexts.has(message);
    }

    abstract displayToast(
        message: string,
        color: 'success' | 'danger' | 'warning' | 'info',
        icon?: string,
        translucent?: boolean,
        duration?: number,
    ): Promise<void>;
    abstract showSuccess(message: string): Promise<void>;
    abstract showError(message: string): Promise<void>;
    abstract showYesOrNoQuestion(title: string, content: string): Promise<boolean>;
    abstract showDialog(title: string, content: string, actions: unknown[]): Promise<object>;
}
